import {Stack, Typography, useTheme} from '@mui/material';
import React from 'react';

import {OdontoBookingTaggingEnum} from 'main/analytics/gaTagsEnum';
import {SummaryCard, SummaryCardSection} from 'presentation/components';
import {useBeneficiaryCard, usePersonalData} from 'presentation/hooks';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useMonitoringTime} from 'presentation/pages/private/hooks/useMonitoringTime';
import {translations} from 'presentation/translations-new';
import {
	formatMasks,
	formatText,
	getBookingTypeByFlowType,
	getUserInfoFromToken,
} from 'presentation/utils';
import {FlowEnum} from '../../constants';

import {ChangeFlowVariantEnum} from '../ChangeFlowDialog/constants';
import {OdontoRecommendations} from '../OdontoRecommendations';
import {ReviewCard} from '../ReviewCard';
import {SummaryDistrictSection} from '../SummaryDistrictSection';
import {SummarySectionGroupProps} from './types';
import {formatOdontoRecommendations} from './utils/formatOdontoRecommendations';
import {useBookingTheme} from 'presentation/modules/booking/shared/hooks/useBookingTheme';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useNavigate} from 'react-router-dom';
import {LOCATION_ROUTE} from 'presentation/modules/booking/shared/constants/bookingLocationPath';
import {useResetBookingFlow} from 'presentation/modules/booking/shared/hooks/useResetBookingFlow';
import {AppointmenInstructionsAlertInfo} from 'presentation/modules/booking/shared/ui/components/AppointmenInstructionsAlertInfo';
import {useSummaryChangeDrawerStore} from 'presentation/modules/booking/shared/hooks/useBookingSummary/store/useSummaryChangeDrawerStore';

const {appointmentCity} = translations['pt-br'].privateFlow.profile;
const {appointmentSummary, beneficiaryData, cardCode} =
	translations['pt-br'].privateFlow.bookingFlow;
const {
	specialty,
	attendanceUnit,
	observations,
	exam,
	contactInfo,
	beneficiaryPlan,
	attendance,
	usingYourLocation,
} = translations['pt-br'].commons;
const {appointmentInstructions} =
	translations['pt-br'].privateFlow.myAppointments.myAppointmentDetails;

export function SummarySectionGroup({
	flowType,
	stepLabels,
	activeStep,
	handleChangeModals,
	disableGutters,
	showTitle,
	noBorderRadius,
}: SummarySectionGroupProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const {beneficiaryCardPlanData} = useBeneficiaryCard();
	const {currentEmail, currentPhone} = usePersonalData();
	const bookingTheme = useBookingTheme({flowType});
	const {colors} = useTheme();
	const activeBookingFlowStep = useBookingFlowStepsStore(state => state.bookingStep);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const navigate = useNavigate();
	const {resetBookingFlow} = useResetBookingFlow();
	const setOpenDrawerState = useSummaryChangeDrawerStore(state => state.setSummaryChangeDrawer);

	const isLastStep = activeStep === stepLabels.length - 1;
	const isTelehealth = flowType === FlowEnum.TELEHEALTH;
	const isOdonto = flowType === FlowEnum.ODONTO;
	const isExam = flowType === FlowEnum.EXAM;
	const isHealth = flowType === FlowEnum.HEALTH;

	const {goToTelehealthAvailability} = useMonitoringTime(bookingData.dateAt);
	const showEnterRoom = isTelehealth && goToTelehealthAvailability;
	const isUsingGeolocation = !bookingData.isManualLocation && (isExam || isHealth || isOdonto);

	const showSpecialtyField = () => {
		if (isTelehealth) return activeStep > 0 && !isLastStep;

		return activeStep > 1 && !isLastStep;
	};

	const getShowUnitField = () => {
		if (isTelehealth) {
			return activeStep >= 1;
		}
		return bookingData.isManualLocation ? activeStep > 3 : activeStep > 2;
	};

	const getLocationPrimaryText = () => {
		if (isUsingGeolocation) {
			return usingYourLocation;
		}

		return formatText(bookingData.cityName);
	};

	const getLocationSecondaryText = () => {
		if (isUsingGeolocation) return undefined;

		return formatText(bookingData.ufName);
	};

	const {cdUsuario} = getUserInfoFromToken();
	const {UNIT_STEP, SPECIALITY_OR_EXAM_STEP, LOCATION_STEP} = getStepPosition({
		flowType,
		isManualLocation: bookingData.isManualLocation,
	});

	const handleChangeLocation = () => {
		if (activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP) {
			resetBookingFlow();
			setOpenDrawerState(false);

			if (bookingData.isManualLocation) {
				setActiveBookingFlowStep(LOCATION_STEP);
				return;
			}

			navigate(LOCATION_ROUTE[flowType], {replace: true});
			setActiveBookingFlowStep(LOCATION_STEP);
			return;
		}

		handleChangeModals(ChangeFlowVariantEnum.LOCATION);
	};

	const showUnitField = getShowUnitField() && !isLastStep;
	const showOdontoRecommendations = isOdonto && bookingData.odontoRecommendations && isLastStep;
	const hideLocationField =
		bookingData?.isFavoriteDoctorsFlow &&
		(isHealth || isTelehealth) &&
		activeStep === UNIT_STEP;
	const showDistrictsField =
		bookingData.isManualLocation &&
		(isExam || isHealth || isOdonto) &&
		activeStep === UNIT_STEP &&
		bookingData.districtName;

	if (!isLastStep) {
		return (
			<SummaryCard
				noBorderRadius={noBorderRadius}
				showTitle={showTitle}
				disableGuters={disableGutters}
				title={appointmentSummary}
				hideAction>
				<SummaryCardSection
					hideAction={true}
					disableGutters={disableGutters}
					hasDivider
					subheader={contactInfo}
					primaryText={formatMasks('phone', currentPhone)}
					iconName="phone-android-filled"
					disableSecondaryTextFormat
					secondaryData={{
						primaryText: currentEmail,
						iconName: 'mail-filled',
					}}
				/>
				{showSpecialtyField() ? (
					<SummaryCardSection
						hideAction={bookingData.isRebooking || bookingData?.isFavoriteDoctorsFlow}
						handleAction={() => handleChangeModals(ChangeFlowVariantEnum.SPECIALTY)}
						disableGutters={disableGutters}
						hasDivider
						subheader={isExam ? exam : specialty}
						primaryText={bookingData.specialtyName}
						iconName={bookingTheme.iconName}
						dataTagId={`${OdontoBookingTaggingEnum.CLICK_ALTERAR_ESPECIALIDADE}${flowType}`}
					/>
				) : null}

				{isTelehealth || hideLocationField ? null : (
					<SummaryCardSection
						dataTagId={`${OdontoBookingTaggingEnum.CLICK_ALTERAR_LOCAL_DE_ATENDIMENTO}${flowType}`}
						hideAction={bookingData.isRebooking || bookingData?.isFavoriteDoctorsFlow}
						handleAction={handleChangeLocation}
						disableGutters={disableGutters}
						hasDivider
						subheader={appointmentCity}
						primaryText={getLocationPrimaryText()}
						disablePrimaryTextFormat={isUsingGeolocation}
						secondaryText={getLocationSecondaryText()}
						iconName="my-location-filled"
					/>
				)}

				{showDistrictsField ? (
					<SummaryDistrictSection
						flowType={flowType}
						dataTagId={`${OdontoBookingTaggingEnum.CLICK_ALTERAR_BAIRRO}${flowType}`}
					/>
				) : null}
				{showUnitField ? (
					<SummaryCardSection
						hideAction={
							bookingData.isRebooking ||
							isTelehealth ||
							bookingData?.isFavoriteDoctorsFlow
						}
						handleAction={() => handleChangeModals(ChangeFlowVariantEnum.UNIT)}
						disableGutters={disableGutters}
						hasDivider
						subheader={isTelehealth ? attendance : attendanceUnit}
						primaryText={bookingData.unitName}
						iconName={isTelehealth ? 'remember-me-filled' : 'location-on-filled'}
						secondaryText={bookingData.unitAddress}
					/>
				) : null}

				{showOdontoRecommendations && bookingData.odontoRecommendations ? (
					<OdontoRecommendations
						subheader={observations}
						hasDivider
						recommendations={formatOdontoRecommendations([
							bookingData.odontoRecommendations,
						])}
					/>
				) : null}
			</SummaryCard>
		);
	}

	return (
		<Stack gap={2}>
			<Typography variant="subtitle2" color={colors.light.text.secondary}>
				{beneficiaryData}
			</Typography>
			<ReviewCard.Root>
				<ReviewCard.Header subheader={beneficiaryPlan} />
				<Stack gap={'16px'}>
					<ReviewCard.Content
						iconName={'account-circle-filled'}
						primaryText={formatText(
							beneficiaryCardPlanData.userName,
						)}></ReviewCard.Content>
					<ReviewCard.Content
						iconName={'hapvida-logo'}
						primaryText={formatText(beneficiaryCardPlanData.planName)}
						secondaryText={`${cardCode}${cdUsuario}`}></ReviewCard.Content>
				</Stack>
			</ReviewCard.Root>
			<ReviewCard.Root>
				<ReviewCard.Header subheader={contactInfo} />
				<Stack gap={'8px'}>
					<ReviewCard.Content
						iconName={'phone-android-filled'}
						primaryText={formatMasks('phone', currentPhone)}></ReviewCard.Content>
					<ReviewCard.Content
						iconName={'mail-filled'}
						primaryText={currentEmail}
						disablePrimaryTextFormat></ReviewCard.Content>
				</Stack>
			</ReviewCard.Root>
			<Stack gap={2}>
				<Typography variant="subtitle2" color={colors.light.text.secondary}>
					{appointmentInstructions.title}
				</Typography>
				<AppointmenInstructionsAlertInfo
					showEnterRoom={showEnterRoom}
					bookingType={getBookingTypeByFlowType(flowType)}
					specialtyCode={bookingData.specialtyCode}
					specialtyName={bookingData.specialtyName}
				/>
			</Stack>
		</Stack>
	);
}
